import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Button, Loader, Modal } from '../../../../Components/uielements';

import {
  OutlineButton,
  ModalButtonWrapper,
  ModalGap,
} from '../../../../services/settings/appStyle/global.style';
import { Form } from '@kuzmycz/react-form-ula';
import BioPlusTextField from '../../../../Components/form-fields/BioPlusTextField';
import { IntlMessages } from '../../../../helper/langHelper';
import MultiSelectDropdown from '../../../../Components/uielements/multiSelectDropdown';
import { ModalLabel } from '../../UserV2/user.style';
import { createGroup, updateGroup } from '../../../../services/bioplus-services/group';
import { businessUserList } from '../../../../services/bioplus-services/friendList';
import { ModalWrapper } from '../group.style';
import { useUserContext } from '../../../../hooks/UserContext';
import { useCompany } from '../../../../hooks/CompanyContext';

const GroupCreationModal = ({ onClose, groupData, isEdit = false }) => {
  const [error, setError] = useState();
  const [success, setSuccess] = useState(false);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const queryClient = useQueryClient();

  const userContext = useUserContext();
  const companyContext = useCompany();
  const companyFilter = companyContext?.company?._id;

  const role = userContext?.company?.role;
  // const userType = role;
  const userType = companyFilter ? 'OWNER' : role === 'EMPLOYEE' ? 'OWNER' : role;
  const companyId = companyFilter ?? userContext?.company?.companyId?._id;

  // Fetch friends list
  const friendsQuery = useQuery(['businessUserList'], () =>
    businessUserList({ companyId, userType })
  );
  const friends = friendsQuery?.data ?? [];

  // Transform friends data to match MultiSelectDropdown format
  const transformFriendsData = (friendsData) => {
    return friendsData.map((friend) => ({
      label: `${friend?.firstName} ${friend?.lastName}`,
      value: friend?._id,
      email: friend?.userId?.email,
    }));
  };

  // Pre-populate selected friends when editing
  useEffect(() => {
    if (isEdit && groupData?.userArray && friends.length > 0) {
      const preSelectedFriends = groupData.userArray.map((user) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user._id,
        email: user.email,
      }));
      setSelectedFriends(preSelectedFriends);
    }
  }, [isEdit, groupData, friends]);

  // Create/Update group mutation
  const groupMutation = useMutation(
    (payload) => {
      if (isEdit && groupData?._id) {
        return updateGroup({ ...payload, groupId: groupData._id });
      }
      return createGroup(payload);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries('listGroupMembers');
        setSuccess(true);
      },
      onError: (e) => {
        setError(e?.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} group`);
      },
    }
  );

  const validator = () => {
    return undefined;
  };

  const handleSubmitGroup = async (values) => {
    if (selectedFriends.length === 0) {
      setError('Please select at least one friend');
      return;
    }

    const payload = {
      name: values.name,
      userArray: selectedFriends.map((friend) => friend.value),
      ...(isEdit === false && { userType, companyId, groupType: 'BUSINESS' }),
    };

    groupMutation.mutate(payload);
  };

  return (
    <Modal isOpen={true} title={isEdit ? 'Edit Group' : 'Create Group'} overFlowVisible={true}>
      <Form
        initialValues={{
          name: isEdit && groupData?.name ? groupData.name : '',
        }}
        validator={validator}
        onSubmit={handleSubmitGroup}
      >
        {success ? (
          <ModalWrapper>
            <ModalLabel>Group {isEdit ? 'updated' : 'created'} successfully!</ModalLabel>
            <ModalButtonWrapper>
              <Button varient="normal" onClick={onClose}>
                Close
              </Button>
            </ModalButtonWrapper>
          </ModalWrapper>
        ) : error ? (
          <ModalWrapper>
            <ModalLabel style={{ color: 'red' }}>{error}</ModalLabel>
            <ModalButtonWrapper>
              <Button varient="normal" onClick={() => setError(null)}>
                Try Again
              </Button>
              <OutlineButton onClick={onClose}>Close</OutlineButton>
            </ModalButtonWrapper>
          </ModalWrapper>
        ) : (
          <ModalWrapper>
            <ModalGap>
              <BioPlusTextField name="name" label="Group Name" fullWidth />

              <MultiSelectDropdown
                options={friendsQuery.isLoading ? [] : transformFriendsData(friends)}
                selectedItems={selectedFriends}
                onSelectionChange={setSelectedFriends}
                label="Select Friends"
                placeholder="Choose friends to add to group..."
                isKeepDropdownOpen={true}
                selectedLabel={'friends'}
              />
            </ModalGap>

            <ModalButtonWrapper>
              <OutlineButton onClick={onClose}>Close</OutlineButton>
              {groupMutation.isLoading ? (
                <Button varient="normal" disabled={true}>
                  {isEdit ? 'Updating...' : 'Creating...'}
                  <Loader size="small" isNotRequiredLoadingText={true} />
                </Button>
              ) : (
                <Button varient="normal" type="submit">
                  <IntlMessages
                    id={isEdit ? 'button.text.update' : 'button.label.group.creation'}
                    defaultMessage={isEdit ? 'Update' : 'Create Group'}
                  />
                </Button>
              )}
            </ModalButtonWrapper>
          </ModalWrapper>
        )}
      </Form>
    </Modal>
  );
};

export default GroupCreationModal;
